import { useWebSocketContext } from '../../contexts/WebSocketContext';
import { cn } from '../../lib/utils';

/**
 * Props for the ConnectionStatus component
 */
interface ConnectionStatusProps {
  /** Additional CSS classes */
  className?: string;
  /** Whether to show the status text (default: true) */
  showText?: boolean;
  /** Position of the indicator (default: 'fixed') */
  position?: 'fixed' | 'relative' | 'static';
}

/**
 * Connection status indicator component
 *
 * Displays a visual indicator of the WebSocket connection state.
 * Shows a pulsing green dot when connected, red dot when disconnected,
 * and yellow dot when connecting or in error state.
 *
 * @example
 * ```tsx
 * // Fixed position in corner (default)
 * <ConnectionStatus />
 *
 * // Inline with text hidden
 * <ConnectionStatus position="relative" showText={false} />
 *
 * // In a header
 * <ConnectionStatus position="static" className="ml-auto" />
 * ```
 */
export function ConnectionStatus({
  className,
  showText = true,
  position = 'fixed',
}: ConnectionStatusProps) {
  const { isConnected, connectionState, reconnect } = useWebSocketContext();

  // Determine indicator color and animation based on state
  const getIndicatorStyles = (): string => {
    switch (connectionState) {
      case 'connected':
        return 'bg-green-500 animate-pulse';
      case 'connecting':
        return 'bg-yellow-500 animate-pulse';
      case 'error':
        return 'bg-red-500';
      case 'disconnected':
      default:
        return 'bg-gray-400';
    }
  };

  // Determine container background based on state
  const getContainerStyles = (): string => {
    switch (connectionState) {
      case 'connected':
        return 'bg-green-50 border-green-200 text-green-700';
      case 'connecting':
        return 'bg-yellow-50 border-yellow-200 text-yellow-700';
      case 'error':
        return 'bg-red-50 border-red-200 text-red-700';
      case 'disconnected':
      default:
        return 'bg-gray-50 border-gray-200 text-gray-600';
    }
  };

  // Get status text
  const getStatusText = (): string => {
    switch (connectionState) {
      case 'connected':
        return 'Live';
      case 'connecting':
        return 'Connecting...';
      case 'error':
        return 'Connection Error';
      case 'disconnected':
      default:
        return 'Offline';
    }
  };

  // Position classes
  const positionClasses: Record<string, string> = {
    fixed: 'fixed bottom-4 right-4 z-50',
    relative: 'relative',
    static: '',
  };

  // Handle click to reconnect when in error or disconnected state
  const handleClick = (): void => {
    if (connectionState === 'error' || connectionState === 'disconnected') {
      reconnect();
    }
  };

  const isClickable = connectionState === 'error' || connectionState === 'disconnected';
  const statusText = getStatusText();

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-3 py-1.5 rounded-full border shadow-sm transition-all duration-300',
        positionClasses[position],
        getContainerStyles(),
        isClickable && 'cursor-pointer hover:opacity-80',
        className
      )}
      onClick={isClickable ? handleClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleClick();
              }
            }
          : undefined
      }
      title={
        isClickable
          ? 'Click to reconnect'
          : isConnected
          ? 'Real-time updates enabled'
          : undefined
      }
      aria-label={`Connection status: ${statusText}`}
    >
      {/* Status indicator dot */}
      <div
        className={cn(
          'w-2 h-2 rounded-full transition-colors duration-300',
          getIndicatorStyles()
        )}
        aria-hidden="true"
      />

      {/* Status text */}
      {showText && (
        <span className="text-xs font-medium select-none">{statusText}</span>
      )}
    </div>
  );
}

export default ConnectionStatus;
